/**
 * Centralised route configuration.
 *
 * Post-dash-v2-rebuild: only public, auth, marketing, trust-link, legal,
 * and mobile pages are exported here. The old dashboard pages are quarantined
 * under src/pages/_legacy/ and excluded from the build via tsconfig.app.json.
 * The new v2 dashboard pages will be added to this barrel as they land.
 */

import { lazyRetry } from "@/lib/lazy-retry";
import { ensureOwnershipClaimsLocaleLoaded } from "@/i18n/loadOwnershipClaimsLocale";

// Eager: only truly critical-path pages that must render
export { default as NotFound } from "@/pages/NotFound";
export { default as AuthCallback } from "@/pages/AuthCallback";
export { default as Index } from "@/pages/Index";
export const Login = lazyRetry(() => import("@/pages/Login"), "Login");
export const Signup = lazyRetry(() => import("@/pages/Signup"), "Signup");
// Dedicated team-member account-creation page (invite link lands here).
export const TeamInviteAcceptPage = lazyRetry(() => import("@/pages/TeamInviteAcceptPage"), "TeamInviteAcceptPage");
// Post-signup role picker: shown once after email verification or Google OAuth
// for brand-new users, before they enter the onboarding wizard.
export const SignupRolePicker = lazyRetry(() => import("@/pages/SignupRolePicker"), "SignupRolePicker");

// Auth
export const MfaVerify = lazyRetry(() => import("@/pages/MfaVerify"));
export const ForgotPassword = lazyRetry(() => import("@/pages/ForgotPassword"));
export const ResetPassword = lazyRetry(() => import("@/pages/ResetPassword"));
export const VerifyEmail = lazyRetry(() => import("@/pages/VerifyEmail"));

// Marketing + public content
export const DocsPage = lazyRetry(() => import("@/pages/DocsPage"));
export const AboutPage = lazyRetry(() => import("@/pages/AboutPage"));
export const PricingPage = lazyRetry(() => import("@/pages/PricingPage"));
export const CheckoutPage = lazyRetry(() => import("@/pages/CheckoutPage"));
export const SwitchingFromCarrier411Page = lazyRetry(() => import("@/pages/SwitchingFromCarrier411Page"));
// Objection-handling page · /vs. Answers "we already pay for a vetting
// platform" and "why not just pull FMCSA myself". See the copy rules in the
// page header before editing any claim on it.
export const VsAlternativesPage = lazyRetry(() => import("@/pages/VsAlternativesPage"));
export const CoveragePage = lazyRetry(() => import("@/pages/CoveragePage"));
export const AgentSignupPage = lazyRetry(() => import("@/pages/AgentSignupPage"));
export const ConnectPage = lazyRetry(() => import("@/pages/ConnectPage"));
export const ClaimAgentPage = lazyRetry(() => import("@/pages/ClaimAgentPage"));
export const CarrierOnboarding = lazyRetry(() => import("@/pages/CarrierOnboarding"));
export const DemoVideoPage = lazyRetry(() => import("@/pages/DemoVideoPage"));
export const DashboardDemoPage = lazyRetry(() => import("@/pages/DashboardDemoPage"));
export const IntegrationGuidePage = lazyRetry(() => import("@/pages/IntegrationGuidePage"));
export const BlogIndex = lazyRetry(() => import("@/pages/BlogIndex"));
export const BlogArticle = lazyRetry(() => import("@/pages/BlogArticle"));
export const BookDemoPage = lazyRetry(() => import("@/pages/BookDemoPage"));
export const PublicCarrierLookup = lazyRetry(() => import("@/pages/PublicCarrierLookup"));
// Programmatic SEO landing pages: /carriers/:region and /brokers/:region.
// One template, ~30 jurisdictions × 2 roles. See src/lib/jurisdictions.ts.
export const StateRegistryPage = lazyRetry(() => import("@/pages/StateRegistryPage"));
// CR Registry · public registry home (Wave 2.7).
// Mounted at /registry. Feature-flagged in App.tsx via `public_registry`.
export const RegistryHomePage = lazyRetry(() => import("@/pages/public/RegistryHomePage"));
// CR Registry · public registry detail (Wave 2.8) — /registry/:cr_id.
// Feature-flagged via `public_registry` (parent flag).
export const RegistryDetailPage = lazyRetry(() =>
  Promise.all([import("@/pages/public/RegistryDetailPage"), ensureOwnershipClaimsLocaleLoaded()]).then(([mod]) => mod),
);
// CR Registry · SEO-canonical profile (Wave 2.8) —
// /registry/cr/:cr_id/:slug. Feature-flagged via `registry_seo_pages`.
// Same data as the detail page but rendered with semantic HTML and
// schema.org Organization markup so search engines index it.
export const RegistrySeoPage = lazyRetry(() => import("@/pages/public/RegistrySeoPage"));
export const QuickstartPage = lazyRetry(() => import("@/pages/QuickstartPage"));
export const ProductPage = lazyRetry(() => import("@/pages/ProductPage"));
export const UnsubscribePage = lazyRetry(() => import("@/pages/UnsubscribePage"));
// Homepage v2 preview · /preview-home. Temporary public route until the
// new homepage replaces /. Delete this export and its route in App.tsx
// once the swap is approved.
export const HomeV2 = lazyRetry(() => import("@/pages/HomeV2"));
// Homepage conversion rebuild · /homepage2. Standalone preview built from the
// "Homepage Conversion Rebuild" brief (May 2026). Not wired to / — the live
// homepage stays untouched while this is reviewed. See src/pages/Homepage2.tsx.
export const Homepage2 = lazyRetry(() => import("@/pages/Homepage2"));

// Public driver trust link page (no login required). The by-ID carrier/broker
// profile pages (CarrierProfilePage / BrokerProfilePage) were removed 2026-06-19
// because they leaked canonical trust scores with no login/credits/rate limit;
// their /carrier/:mc and /broker/:mc routes now redirect to /verify.
export const TrustLinkDriverPage = lazyRetry(() => import("@/pages/TrustLinkDriverPage"));
// Public Canadian broker profile (CABrokerProfilePage, /brokers/ca/:cab_id) was
// removed 2026-06-19 alongside the carrier/broker by-ID pages (same trust-score
// leak); the route now redirects to /verify.
export const ClaimVerifyPage = lazyRetry(() => import("@/pages/ClaimVerifyPage"));
export const TrustLinkReceiptPage = lazyRetry(() => import("@/pages/TrustLinkReceiptPage"));
// Public trust-report verification — /verify/report/:reportNo (?t=token).
// The landing surface for the "Copy verify link" action on PDF trust reports.
export const VerifyReportPage = lazyRetry(() => import("@/pages/VerifyReportPage"));
// Packet share recipient view — /share/report/:shareToken (revocable expiring
// capability minted by report-share; dark behind PACKET_SHARE_ENABLED's
// backend sibling, so an unarmed link renders the not-found state).
export const SharedReportPage = lazyRetry(() => import("@/pages/SharedReportPage"));

// Legal
export const SecurityPage = lazyRetry(() => import("@/pages/SecurityPage"));
export const TermsPage = lazyRetry(() => import("@/pages/TermsPage"));
export const PrivacyPage = lazyRetry(() => import("@/pages/PrivacyPage"));
export const ApiTermsPage = lazyRetry(() => import("@/pages/ApiTermsPage"));
export const CookiePolicyPage = lazyRetry(() => import("@/pages/CookiePolicyPage"));

// Trust pages · canonical truth instead of external subdomain
// (ChangelogPage hidden 2026-06-25 — /changelog redirects to /status in App.tsx)
export const StatusPage = lazyRetry(() => import("@/pages/StatusPage"));

// Mobile views (public, token-scoped)
export const MobileLayout = lazyRetry(() => import("@/pages/mobile/MobileLayout"));
export const MobileLoadFeed = lazyRetry(() => import("@/pages/mobile/MobileLoadFeed"));
export const MobileProfile = lazyRetry(() => import("@/pages/mobile/MobileProfile"));
export const MobileAlerts = lazyRetry(() => import("@/pages/mobile/MobileAlerts"));
